/**
 * SizeConfigModal — F10 Size Configuration
 * Matches HTML prototype: size range + per-step Rate/MRP increments
 *
 * Workflow:
 *  1. Fill Classification (Division/Dept/Category/etc.)
 *  2. Fill Supplier & Tax, UOM & HSN
 *  3. Set Size Range: From=10 To=30 Incr=1 Qty=1
 *  4. Set Rate: From=1200 (+)=50 (-)=0  →  Size 10→1200, 11→1250, 12→1300…
 *  5. Set MRP:  From=3000 (+)=50 (-)=0  →  Size 10→3000, 11→3050…
 *  6. Optional: Add extra attributes (Color etc.) → cartesian combinations
 *  7. Click "＋ Add (Generate Rows)" → preview table with Markup%/Markdown%
 *  8. Click "Save All Sizes" → push every row into item grid
 */
import React, { useState, useRef } from 'react';
import ImageManagerModal from './ImageManagerModal';
import { loadImages } from '../../lib/imageStorage';

function blankRow() {
  return {
    _id: Date.now() + Math.random(), gstType: 'excl',
    code: '', hsn: '', name: '', artCode: '', oemCode: '', uom: '',
    qty: '', rate: '', price: '', mrp: '', wsp: '', disc: '0', pn: 'P',
    cgst: '2.50', sgst: '2.50', amount: '',
    division: '', dept: '', category: '', subcatg: '', brand: '', style: '',
    size: '', color: '', design: '', cref: '',
    smToken: '', commission: '0.00', scheme: '', batchNo: '', mfgDate: '', expiryDate: '', remarks: '',
  };
}
function fmtN(v, d = 2) { return (parseFloat(v) || 0).toFixed(d); }

export default function SizeConfigModal({ selRow, onSaveAll, onClose, sourceLabel = 'Procurement' }) {
  const imgInputRef = useRef(null);
  const [imgUrl,    setImgUrl]    = useState(null);
  const [showImgMgr, setShowImgMgr] = useState(false);
  const [generated, setGenerated] = useState([]);

  // ── Classification ──
  const [cls, setCls] = useState({
    division: selRow?.division || 'Cloths',
    dept:     selRow?.dept     || '',
    category: selRow?.category || '',
    subcatg:  selRow?.subcatg  || '',
    brand:    selRow?.brand    || '',
    style:    selRow?.style    || '',
    color:    selRow?.color    || 'Black',
    design:   selRow?.design   || '',
    cref:     selRow?.cref     || '',
  });

  const imgKey = `sizecfg_${cls.division||'item'}_${Date.now().toString().slice(-6)}`;

  // ── Supplier & Tax, UOM & HSN ──
  const [tax, setTax] = useState({ supplier: '', inputTax: '5', tax: '18' });
  const [uom, setUom] = useState({ uom: 'Qty', scanQty: '1', hsnCode: '', allSizes: false });

  // ── Size Range & Pricing ──
  const [range, setRange] = useState({
    fromSize: '10', toSize: '30', incrSize: '1', qty: '1',
    rateFrom: '1200', ratePlus: '50', rateMinus: '0', tokens: '10',
    mrpFrom: '3000', mrpPlus: '50', mrpMinus: '0',
    discOn: true, discPct: '10',
  });

  // ── Extra attributes (optional) ──
  const [extraAttrs, setExtraAttrs] = useState([]);

  function addExtraAttr() {
    setExtraAttrs(a => [...a, { name: '', values: '' }]);
  }
  function setExtraAttr(i, k, v) {
    setExtraAttrs(a => a.map((x, j) => j === i ? { ...x, [k]: v } : x));
  }
  function removeExtraAttr(i) {
    setExtraAttrs(a => a.filter((_, j) => j !== i));
  }

  // ── Generate ──
  function generate() {
    const from   = parseFloat(range.fromSize) || 0;
    const to     = parseFloat(range.toSize)   || 0;
    const incr   = parseFloat(range.incrSize) || 1;
    const qty    = parseFloat(range.qty)      || 1;
    const rF     = parseFloat(range.rateFrom) || 0;
    const rP     = parseFloat(range.ratePlus) || 0;
    const rM     = parseFloat(range.rateMinus)|| 0;
    const mF     = parseFloat(range.mrpFrom)  || 0;
    const mP     = parseFloat(range.mrpPlus)  || 0;
    const mM     = parseFloat(range.mrpMinus) || 0;
    const discPct= range.discOn ? parseFloat(range.discPct) || 0 : 0;

    // Build size list
    const sizes = [];
    for (let s = from; incr > 0 ? s <= to + 0.0001 : s >= to - 0.0001; s += incr) {
      sizes.push(parseFloat(s.toFixed(4)));
      if (sizes.length > 500) break; // safety
    }

    // Build extra attribute combos
    const extraCombos = extraAttrs
      .filter(a => a.name && a.values)
      .reduce((acc, attr) => {
        const vals = attr.values.split(',').map(s => s.trim()).filter(Boolean);
        if (!acc.length) return vals.map(v => ({ [attr.name]: v }));
        return acc.flatMap(c => vals.map(v => ({ ...c, [attr.name]: v })));
      }, []);

    const rows = [];
    sizes.forEach((size, si) => {
      const rate = rF + (rP - rM) * si;
      const mrp  = mF + (mP - mM) * si;
      const markup   = rate > 0 ? ((mrp - rate) / rate * 100) : 0;
      const markdown = mrp  > 0 ? ((mrp - rate) / mrp  * 100) : 0;
      const combos   = extraCombos.length > 0 ? extraCombos : [{}];
      combos.forEach(combo => {
        rows.push({
          _id:       Math.random(),
          size:      String(size),
          costPrice: fmtN(rate),
          qty:       qty,
          mrp:       fmtN(mrp),
          pn:        'P',
          disc:      fmtN(discPct),
          splMrp:    '0.00',
          markup:    fmtN(markup),
          markdown:  fmtN(markdown),
          ...combo,   // extra attrs (Color, etc.)
        });
      });
    });
    setGenerated(rows);
  }

  function clearGenerated() { setGenerated([]); }

  function saveAll() {
    const rows = generated.map(r => ({
      ...blankRow(),
      ...cls,
      uom:          uom.uom,
      hsn:          uom.hsnCode,
      size:         r.size,
      qty:          String(r.qty),
      rate:         r.costPrice,
      mrp:          r.mrp,
      disc:         r.disc,
      cgst:         String(parseFloat(tax.inputTax || 0) / 2),
      sgst:         String(parseFloat(tax.inputTax || 0) / 2),
      smToken:      range.tokens,
      // extra attrs (Color etc.)
      ...(r.Color ? { color: r.Color } : {}),
      ...(r.Design ? { design: r.Design } : {}),
    }));
    onSaveAll(rows);
    onClose();
  }

  const setR = (k, v) => setRange(p => ({ ...p, [k]: v }));
  const setC = (k, v) => setCls(p => ({ ...p, [k]: v }));

  const clsLeft  = ['division','dept','category','subcatg','brand','style'];
  const clsRight = ['color','design','cref'];

  return (
    <>
      <div className="pi2-overlay-bg" onClick={onClose} />
      <div className="pi2-modal-wrap">
        <div className="pi2-modal" style={{ width: 980, maxWidth: '98vw' }}>

          {/* Header */}
          <div className="pi2-modal-head">
            <div className="t">
              <small>Item Master · F10</small>
              Size Configuration — generate a size range in one go
            </div>
            <button className="x" onClick={onClose}>✕</button>
          </div>

          {/* Toast */}
          <div className="pi2-modal-toast">
            ℹ️ Set the size range and per-step Rate/MRP increments, click <b>Add</b> to generate rows,
            repeat for another range if needed, then <b>Save All Sizes</b> to push every generated row
            into the item grid as its own line.
          </div>

          <div className="pi2-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* Item Image */}
            <div className="pi2-card">
              <div className="pi2-cardhead">
                <span className="pi2-dot" />Item Image
                <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontSize: 10, color: 'var(--pi2-ink-soft)' }}>
                  &nbsp;(applies to every size generated below)
                </span>
              </div>
              <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                {imgUrl
                  ? <img src={imgUrl} alt="item" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--pi2-border)' }} />
                  : <button type="button" style={{ width: 52, height: 52, border: '2px dashed var(--pi2-border)', borderRadius: 4, background: 'none', cursor: 'pointer', fontSize: 24 }}
                      onClick={() => setShowImgMgr(true)}>📷</button>
                }
                <button className="pi2-addfld" type="button" onClick={() => setShowImgMgr(true)}>
                  🖼 Upload / Crop / Link
                </button>
              </div>
            </div>

            {/* Classification */}
            <div className="pi2-card">
              <div className="pi2-cardhead">
                <span className="pi2-dot" />Select Group &amp; Categories
                <button className="pi2-addfld" type="button"><span>+</span>Add Field</button>
              </div>
              <div className="pi2-fieldgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                {clsLeft.map(k => (
                  <div className="pi2-field" key={k}>
                    <label style={{ textTransform: 'capitalize' }}>{k === 'subcatg' ? 'Sub Catg' : k}</label>
                    <input value={cls[k]} onChange={e => setC(k, e.target.value)} />
                  </div>
                ))}
                {clsRight.map(k => (
                  <div className="pi2-field" key={k}>
                    <label style={{ textTransform: 'capitalize' }}>{k === 'cref' ? 'C.REF' : k}</label>
                    <input value={cls[k]} onChange={e => setC(k, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>

            {/* Supplier & Tax + UOM & HSN */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="pi2-card">
                <div className="pi2-cardhead"><span className="pi2-dot" />Supplier &amp; Tax</div>
                <div className="pi2-fieldgrid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  <div className="pi2-field"><label>Supplier *</label>
                    <input value={tax.supplier} onChange={e => setTax(p => ({ ...p, supplier: e.target.value }))} /></div>
                  <div className="pi2-field"><label>Input Tax *</label>
                    <input className="pi2-mono" value={tax.inputTax} onChange={e => setTax(p => ({ ...p, inputTax: e.target.value }))} /></div>
                  <div className="pi2-field"><label>Tax *</label>
                    <input className="pi2-mono" value={tax.tax} onChange={e => setTax(p => ({ ...p, tax: e.target.value }))} /></div>
                </div>
              </div>
              <div className="pi2-card">
                <div className="pi2-cardhead"><span className="pi2-dot" />UOM &amp; HSN</div>
                <div className="pi2-fieldgrid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  <div className="pi2-field"><label>UOM</label>
                    <input value={uom.uom} onChange={e => setUom(p => ({ ...p, uom: e.target.value }))} /></div>
                  <div className="pi2-field"><label>Scan Qty</label>
                    <input className="pi2-mono" value={uom.scanQty} onChange={e => setUom(p => ({ ...p, scanQty: e.target.value }))} /></div>
                  <div className="pi2-field"><label>HSN Code *</label>
                    <input value={uom.hsnCode} onChange={e => setUom(p => ({ ...p, hsnCode: e.target.value }))} /></div>
                  <div className="pi2-field">
                    <label>
                      <input type="checkbox" style={{ width: 12, height: 12, marginRight: 4 }}
                        checked={uom.allSizes} onChange={e => setUom(p => ({ ...p, allSizes: e.target.checked }))} />
                      All Sizes
                    </label>
                    <span style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>generate every configured size</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Size Range & Pricing Rules */}
            <div className="pi2-card">
              <div className="pi2-cardhead"><span className="pi2-dot" />Size Range &amp; Pricing Rules</div>
              <div className="pi2-fieldgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                {[
                  ['fromSize', 'From Size'], ['toSize', 'To Size'],
                  ['incrSize', 'Incr. Size'], ['qty', 'Qty'],
                  ['rateFrom', 'Rate From'], ['ratePlus', 'Rate (+)'],
                  ['rateMinus', 'Rate (-)'],  ['tokens', 'Tokens'],
                  ['mrpFrom',  'MRP From'],   ['mrpPlus', 'MRP (+)'],
                  ['mrpMinus', 'MRP (-)'],
                ].map(([k, lbl]) => (
                  <div className="pi2-field" key={k}>
                    <label>{lbl}</label>
                    <input className="pi2-mono" value={range[k]} onChange={e => setR(k, e.target.value)} />
                  </div>
                ))}
                {/* Discount with checkbox */}
                <div className="pi2-field">
                  <label>
                    <input type="checkbox" style={{ width: 12, height: 12, marginRight: 4 }}
                      checked={range.discOn} onChange={e => setR('discOn', e.target.checked)} />
                    Discount %
                  </label>
                  <input className="pi2-mono" value={range.discPct}
                    disabled={!range.discOn} onChange={e => setR('discPct', e.target.value)} />
                </div>
              </div>

              {/* Other Attributes */}
              <div className="pi2-cardhead" style={{ borderTop: '1px solid var(--pi2-border)', marginTop: 8 }}>
                Other Attributes
                <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontSize: 10, color: 'var(--pi2-ink-soft)' }}>
                  &nbsp;(optional — combines with Size to generate every combination)
                </span>
                <button className="pi2-addfld" type="button" onClick={addExtraAttr}>
                  <span>+</span>Add Attribute
                </button>
              </div>
              <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {extraAttrs.length === 0
                  ? <span style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>No extra attributes — rows will be generated by Size only.</span>
                  : extraAttrs.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input placeholder="Attribute Name (e.g. Color)" value={a.name} style={{ width: 180 }}
                        onChange={e => setExtraAttr(i, 'name', e.target.value)} />
                      <input placeholder="Values (e.g. Red,Blue,Green)" value={a.values} style={{ flex: 1 }}
                        onChange={e => setExtraAttr(i, 'values', e.target.value)} />
                      <button type="button" onClick={() => removeExtraAttr(i)}
                        style={{ color: '#c00', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13 }}>✕</button>
                    </div>
                  ))
                }
              </div>
              <div style={{ padding: '6px 8px 8px', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="pi2-btn primary" type="button" onClick={generate}
                  data-testid="sizecfg-generate-btn">
                  ＋ Add (Generate Rows)
                </button>
              </div>
            </div>

            {/* Generated Sizes table */}
            <div className="pi2-card">
              <div className="pi2-cardhead">
                <span className="pi2-dot" />Generated Sizes
                <span className={`pi2-rowdetail-tag${generated.length > 0 ? ' active' : ''}`}
                  style={{ marginLeft: 8 }}>{generated.length} rows</span>
                <button className="pi2-addfld" type="button" style={{ marginLeft: 6 }} onClick={clearGenerated}>
                  Clear
                </button>
              </div>
              {generated.length === 0 ? (
                <div style={{ padding: '20px 12px', fontSize: 12, color: 'var(--pi2-ink-soft)', textAlign: 'center' }}>
                  Configure Size Range above and click "＋ Add (Generate Rows)"
                </div>
              ) : (
                <div style={{ overflow: 'auto', maxHeight: 280 }}>
                  <table className="pi2-table" style={{ width: '100%' }} data-testid="sizecfg-generated-table">
                    <thead>
                      <tr>
                        <th>Sl.No</th>
                        <th>Size</th>
                        {extraAttrs.filter(a => a.name).map(a => <th key={a.name}>{a.name}</th>)}
                        <th className="pi2-num">Cost Price</th>
                        <th className="pi2-num">Qty</th>
                        <th className="pi2-num">MRP</th>
                        <th>P/A</th>
                        <th className="pi2-num">Discount</th>
                        <th className="pi2-num">Spl.Mrp</th>
                        <th className="pi2-num">MarkUp %</th>
                        <th className="pi2-num">Markdown %</th>
                        <th style={{ width: 28 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {generated.map((r, ri) => (
                        <tr key={r._id}>
                          <td style={{ textAlign: 'center' }}>{ri + 1}</td>
                          <td>
                            <input className="pi2-cell-edit" value={r.size}
                              onChange={e => setGenerated(g => g.map((x, j) => j === ri ? { ...x, size: e.target.value } : x))} />
                          </td>
                          {extraAttrs.filter(a => a.name).map(a => (
                            <td key={a.name}>{r[a.name] || ''}</td>
                          ))}
                          {['costPrice', 'qty', 'mrp'].map(k => (
                            <td key={k} className="pi2-num">
                              <input className="pi2-cell-edit" style={{ textAlign: 'right', fontFamily: 'monospace' }} value={r[k] || ''}
                                onChange={e => {
                                  setGenerated(g => g.map((x, j) => {
                                    if (j !== ri) return x;
                                    const updated = { ...x, [k]: e.target.value };
                                    const rate = parseFloat(updated.costPrice) || 0;
                                    const mrp  = parseFloat(updated.mrp)       || 0;
                                    updated.markup   = rate > 0 ? fmtN((mrp - rate) / rate * 100) : '0.00';
                                    updated.markdown = mrp  > 0 ? fmtN((mrp - rate) / mrp  * 100) : '0.00';
                                    return updated;
                                  }));
                                }} />
                            </td>
                          ))}
                          <td>
                            <select value={r.pn || 'P'} style={{ border: 'none', background: 'none', fontSize: 11 }}
                              onChange={e => setGenerated(g => g.map((x, j) => j === ri ? { ...x, pn: e.target.value } : x))}>
                              <option value="P">P</option><option value="N">N</option><option value="A">A</option>
                            </select>
                          </td>
                          {['disc', 'splMrp'].map(k => (
                            <td key={k} className="pi2-num">
                              <input className="pi2-cell-edit" style={{ textAlign: 'right', fontFamily: 'monospace' }} value={r[k] || ''}
                                onChange={e => setGenerated(g => g.map((x, j) => j === ri ? { ...x, [k]: e.target.value } : x))} />
                            </td>
                          ))}
                          <td className="pi2-num" style={{ padding: '0 4px', fontFamily: 'monospace', fontSize: 11 }}>{r.markup}</td>
                          <td className="pi2-num" style={{ padding: '0 4px', fontFamily: 'monospace', fontSize: 11 }}>{r.markdown}</td>
                          <td>
                            <button type="button" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#c00', fontSize: 12 }}
                              onClick={() => setGenerated(g => g.filter((_, j) => j !== ri))}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

          {/* Footer */}
          <div className="pi2-modal-foot">
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
              <span><b>F10</b> opens this screen</span>
              <span>Rows below are added to the item grid on save</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="pi2-btn" type="button" onClick={onClose}>Cancel</button>
              <button className="pi2-btn primary" type="button" onClick={saveAll}
                data-testid="sizecfg-save-btn" disabled={generated.length === 0}>
                Save All Sizes ({generated.length})
              </button>
            </div>
          </div>
        </div>
      </div>
      {showImgMgr && (
        <ImageManagerModal
          itemKey={imgKey}
          itemLabel={cls.division ? `${cls.division} · ${cls.category}` : 'Size Config Item'}
          onSave={(ref) => { setImgUrl(ref.firstThumb || null); setShowImgMgr(false); }}
          onClose={() => setShowImgMgr(false)}
        />
      )}
    </>
  );
}